import { useId } from 'react'
import { useIsXtractBrand } from '../context/BrandingContext'

/**
 * Branded loading indicator: a gradient arc orbits the ring with a bright
 * "extracted" particle riding its leading edge, while the core pulses like
 * the data point it was pulled from. Uses the same --ai-gradient-from/to
 * tokens as the rest of the AI/extraction UI, so it follows org branding.
 *
 * Drop-in replacement for the old `animate-spin rounded-full border-*` divs
 * — same box size via `size`, same "just a spinner" footprint at a glance.
 */
export default function ExtractLoader(props) {
  return useIsXtractBrand() ? <XtractMarkLoader {...props} /> : <RingLoader {...props} />
}

/**
 * The Xtract logo in motion (1.8 s loop, styles in index.css under .xl-mark):
 * the document and record squares part along the diagonal and settle back,
 * the overlap where the data sits slides with them, and the two folds blink
 * in turn. It rests on the true logo once per cycle. Only shown while the
 * organisation still carries the Xtract brand; a rebranded one gets the ring.
 */
function XtractMarkLoader({ size = 40, className = '' }) {
  const id = useId().replace(/:/g, '')
  return (
    <svg
      className={`xl-mark ${className}`}
      style={{ width: size, height: size }}
      viewBox="6 6 88 88"
      role="status"
      aria-label="Loading"
    >
      <defs>
        <linearGradient id={`${id}-b`} gradientUnits="userSpaceOnUse" x1="12" y1="12" x2="64" y2="64">
          <stop offset="0" className="xl-b0" /><stop offset="1" className="xl-b1" />
        </linearGradient>
        <linearGradient id={`${id}-f`} gradientUnits="userSpaceOnUse" x1="36" y1="36" x2="88" y2="88">
          <stop offset="0" className="xl-f0" /><stop offset="1" className="xl-f1" />
        </linearGradient>
        <linearGradient id={`${id}-o`} gradientUnits="userSpaceOnUse" x1="36" y1="36" x2="64" y2="64">
          <stop offset="0" className="xl-o0" /><stop offset="1" className="xl-o1" />
        </linearGradient>
        <clipPath id={`${id}-clip`}>
          <path d="M23 12L53 12A11 11 0 0 1 64 23L64 53A11 11 0 0 1 53 64L23 64A11 11 0 0 1 12 53L12 23A11 11 0 0 1 23 12Z" />
        </clipPath>
      </defs>
      <g className="xl-back">
        <path d="M23 12L50.42 12A1.4 1.4 0 0 1 51.41 12.41L63.59 24.59A1.4 1.4 0 0 1 64 25.58L64 53A11 11 0 0 1 53 64L23 64A11 11 0 0 1 12 53L12 23A11 11 0 0 1 23 12Z" fill={`url(#${id}-b)`} />
        <path className="xl-fold xl-fold-in" d="M48.68 18.22A1.1 1.1 0 0 0 46.8 19L46.8 28.1A1.1 1.1 0 0 0 47.9 29.2L57 29.2A1.1 1.1 0 0 0 57.78 27.32Z" />
      </g>
      <g className="xl-front">
        <path d="M77 88L49.58 88A1.4 1.4 0 0 1 48.59 87.59L36.41 75.41A1.4 1.4 0 0 1 36 74.42L36 47A11 11 0 0 1 47 36L77 36A11 11 0 0 1 88 47L88 77A11 11 0 0 1 77 88Z" fill={`url(#${id}-f)`} />
        <path className="xl-fold xl-fold-out" d="M51.32 81.78A1.1 1.1 0 0 0 53.2 81L53.2 71.9A1.1 1.1 0 0 0 52.1 70.8L43 70.8A1.1 1.1 0 0 0 42.22 72.68Z" />
      </g>
      <g className="xl-back">
        <g clipPath={`url(#${id}-clip)`}>
          <g className="xl-overlap">
            <path className="xl-overlap-shape" d="M77 88L47 88A11 11 0 0 1 36 77L36 47A11 11 0 0 1 47 36L77 36A11 11 0 0 1 88 47L88 77A11 11 0 0 1 77 88Z" fill={`url(#${id}-o)`} />
          </g>
        </g>
      </g>
    </svg>
  )
}

function RingLoader({ size = 40, className = '', mini = false }) {
  const gradientId = useId()
  const isMini = mini || size < 28

  return (
    <svg
      className={`extract-loader ${className}`}
      style={{ width: size, height: size }}
      viewBox="0 0 50 50"
      role="status"
      aria-label="Loading"
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="var(--ai-gradient-from)" />
          <stop offset="100%" stopColor="var(--ai-gradient-to)" />
        </linearGradient>
      </defs>

      <circle className="extract-loader-track" cx="25" cy="25" r="20" />

      <g className="extract-loader-spin">
        <circle
          className="extract-loader-arc"
          cx="25"
          cy="25"
          r="20"
          stroke={`url(#${gradientId})`}
        />
        {!isMini && <circle className="extract-loader-particle" cx="25" cy="5" r="2.4" />}
      </g>

      {!isMini && <circle className="extract-loader-core" cx="25" cy="25" r="4" />}
    </svg>
  )
}

